import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import {
  durumDegistir,
  hesapsizPersonel,
  kilitAc,
  kullaniciListesi,
  kullaniciOlustur,
  rolAta,
  rolSonlandir,
  ROLLER,
  sifreSifirla,
  type HesapsizPersonel,
  type KullaniciOzeti,
  type KullaniciSorgusu,
} from '../api/kullanici';
import type { Kimlik } from '../api/tipler';
import { BirimSecici } from '../bilesenler/BirimSecici';
import { Diyalog, GerekceDiyalogu } from '../bilesenler/Diyalog';
import { Sayfalama, Tablo, type Sutun } from '../bilesenler/Tablo';
import { Dugme, HataKutusu, Metin, Sekme } from '../bilesenler/temel';
import { zamanMetni } from '../bicim/tarih';
import { sayfalamaKullan } from '../kancalar/sayfalama';
import { yetkiKullan, yetkilerHazirKullan } from '../yetki/yetki';

/**
 * KULLANICI HESAPLARI.
 *
 * HESAP PERSONELDEN AYRI: her personelin hesabı olmak zorunda değil. Saha
 * personelinin bir bölümü puantörle takip ediliyor ve hiç giriş yapmıyor.
 * "Hesapsız personel" ayrı sekmede çünkü hesap açılacakları bulmak için
 * personel listesini elemek gerekmemeli.
 *
 * YETKİ İKİ SEVİYEDE ve ayrım bilinçli:
 *   hesap açma · şifre sıfırlama · kilit açma   SISTEM_YONET | PERSONEL_YONET
 *   hesap kapatma · ROL VERME                   SISTEM_YONET            ← TEK
 *
 * İK günlük işi yapar; rol yetki demektir ve personel işlemleri yapan biri
 * kendine yetki dağıtamamalı.
 */

type SekmeKodu = 'hesaplar' | 'hesapsiz';

export function Kullanicilar() {
  const yetkilerHazir = yetkilerHazirKullan();

  const gorebilir = yetkiKullan('SISTEM_YONET', 'PERSONEL_YONET');
  const [sekme, sekmeYaz] = useState<SekmeKodu>('hesaplar');

  if (!yetkilerHazir) {
    return <p className="text-sm text-slate-500">Yetkiler yükleniyor…</p>;
  }

  if (!gorebilir) {
    return (
      <p className="rounded-lg border border-slate-200 bg-white p-6 text-center text-sm text-slate-500">
        Bu ekranı görüntüleme yetkiniz yok.
      </p>
    );
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-xl font-semibold text-slate-900">Kullanıcı hesapları</h1>
        <p className="mt-1 text-sm text-slate-600">
          Hesap personelden ayrıdır: puantörle takip edilen personelin hesabı olmayabilir. Hesap
          SİLİNMEZ, pasife alınır.
        </p>
      </header>

      <div className="flex gap-1 border-b border-slate-200">
        <Sekme etkin={sekme === 'hesaplar'} tikla={() => sekmeYaz('hesaplar')} etiket="Hesaplar" />
        <Sekme
          etkin={sekme === 'hesapsiz'}
          tikla={() => sekmeYaz('hesapsiz')}
          etiket="Hesapsız personel"
        />
      </div>

      {sekme === 'hesaplar' ? <HesapListesi /> : <HesapsizListe />}
    </div>
  );
}

/* ------------------------------------------------------------------ */

interface HesapSuzgeci {
  arama?: string;
  aktif?: boolean;
  kilitliMi?: boolean;
  sifreDegistirmemis?: boolean;
  birimId?: Kimlik;
}

function HesapListesi() {
  const sorguIstemcisi = useQueryClient();

  const rolVerebilir = yetkiKullan('SISTEM_YONET');

  const { suzgec, suzgeciYaz, sayfa, sayfayaGit, sayfaBoyutu } = sayfalamaKullan<HesapSuzgeci>({});

  const [rolAcik, rolAcikYaz] = useState<KullaniciOzeti | null>(null);
  const [sifirlanan, sifirlananYaz] = useState<KullaniciOzeti | null>(null);
  const [geciciSifre, geciciSifreYaz] = useState<{ ad: string; sifre: string } | null>(null);

  const sorgu: KullaniciSorgusu = { ...suzgec, sayfa, sayfaBoyutu };

  const liste = useQuery({
    queryKey: ['kullanici', 'liste', sorgu],
    queryFn: () => kullaniciListesi(sorgu),
    placeholderData: keepPreviousData,
  });

  const tazele = () => sorguIstemcisi.invalidateQueries({ queryKey: ['kullanici'] });

  const kilit = useMutation({
    mutationFn: (id: Kimlik) => kilitAc(id),
    onSuccess: tazele,
  });

  const durum = useMutation({
    mutationFn: (girdi: { id: Kimlik; aktif: boolean }) => durumDegistir(girdi.id, girdi.aktif),
    onSuccess: tazele,
  });

  const sifirla = useMutation({
    mutationFn: (girdi: { k: KullaniciOzeti; gerekce: string }) =>
      sifreSifirla(girdi.k.kullaniciId, girdi.gerekce === '' ? undefined : girdi.gerekce),
    onSuccess: async (sonuc, girdi) => {
      // GEÇİCİ ŞİFRE BİR KEZ DÖNER ve hiçbir yerde saklanmaz; ekranda
      // gösterilmezse kaybolur ve yeniden sıfırlamak gerekir.
      geciciSifreYaz({ ad: girdi.k.adSoyad, sifre: sonuc.geciciSifre });
      sifirlananYaz(null);
      await tazele();
    },
  });

  const sutunlar: Sutun<KullaniciOzeti>[] = [
    {
      baslik: 'Kullanıcı',
      hucre: (k) => (
        <span>
          <span className="block font-medium text-slate-900">{k.adSoyad}</span>
          <span className="block font-mono text-xs text-slate-500">
            {k.kullaniciAdi} · {k.personelNo}
          </span>
        </span>
      ),
    },
    {
      baslik: 'Roller',
      hucre: (k) =>
        k.roller.length === 0 ? (
          <span className="text-xs text-slate-400">rol yok</span>
        ) : (
          <span className="flex flex-wrap gap-1">
            {k.roller.map((r) => (
              <span key={r} className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-700">
                {ROLLER.find((x) => x.kod === r)?.ad ?? r}
              </span>
            ))}
          </span>
        ),
    },
    {
      baslik: 'Son giriş',
      hucre: (k) =>
        k.sonGirisZamani === null ? (
          <span className="text-xs text-amber-700">hiç giriş yapmadı</span>
        ) : (
          <span className="whitespace-nowrap text-xs">{zamanMetni(k.sonGirisZamani)}</span>
        ),
      daralt: true,
    },
    {
      baslik: 'Durum',
      hucre: (k) => (
        <span className="flex flex-wrap gap-1">
          {!k.aktif && (
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
              Pasif
            </span>
          )}
          {k.kilitliMi && (
            <span className="rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700">
              Kilitli
            </span>
          )}
          {k.sifreDegistirmeliMi && (
            <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs text-amber-800">
              Şifre değiştirmeli
            </span>
          )}
          {/*
            AYDINLATMA ONAYI KVKK GEREĞİ: onaylanmadan sisteme girilemez.
            Rozet, "hesap açık ama neden giremiyor" sorusunun cevabı.
          */}
          {!k.aydinlatmaOnaylandiMi && (
            <span className="rounded-full bg-orange-50 px-2 py-0.5 text-xs text-orange-800">
              Aydınlatma onayı yok
            </span>
          )}
          {k.aktif && !k.kilitliMi && !k.sifreDegistirmeliMi && k.aydinlatmaOnaylandiMi && (
            <span className="text-xs text-slate-500">Etkin</span>
          )}
        </span>
      ),
    },
    {
      baslik: '',
      hucre: (k) => (
        <span className="flex justify-end gap-1 whitespace-nowrap">
          <Eylemci etiket="Şifre sıfırla" tikla={() => sifirlananYaz(k)} />
          {k.kilitliMi && <Eylemci etiket="Kilidi aç" tikla={() => kilit.mutate(k.kullaniciId)} />}
          {rolVerebilir && <Eylemci etiket="Roller" tikla={() => rolAcikYaz(k)} />}
          {rolVerebilir && (
            <Eylemci
              etiket={k.aktif ? 'Pasife al' : 'Etkinleştir'}
              tehlike={k.aktif}
              tikla={() => durum.mutate({ id: k.kullaniciId, aktif: !k.aktif })}
            />
          )}
        </span>
      ),
      hizala: 'sag',
      daralt: true,
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <Metin
          etiket="Ara"
          deger={suzgec.arama ?? ''}
          degisti={(y) => suzgeciYaz((o) => ({ ...o, arama: y === '' ? undefined : y }))}
          ipucu="Ad, soyad veya kullanıcı adı"
        />
        <div className="w-56">
          <BirimSecici
            deger={suzgec.birimId}
            degisti={(y) => suzgeciYaz((o) => ({ ...o, birimId: y }))}
            bosSecenek="Tüm birimler"
          />
        </div>

        <Onay
          etiket="Yalnız kilitli"
          secili={suzgec.kilitliMi === true}
          degisti={(v) => suzgeciYaz((o) => ({ ...o, kilitliMi: v ? true : undefined }))}
        />
        <Onay
          etiket="Hiç giriş yapmamış"
          secili={suzgec.sifreDegistirmemis === true}
          degisti={(v) => suzgeciYaz((o) => ({ ...o, sifreDegistirmemis: v ? true : undefined }))}
        />
        <Onay
          etiket="Yalnız pasif"
          secili={suzgec.aktif === false}
          degisti={(v) => suzgeciYaz((o) => ({ ...o, aktif: v ? false : undefined }))}
        />
      </div>

      <HataKutusu hata={liste.error} />
      <HataKutusu hata={kilit.error} />
      <HataKutusu hata={durum.error} />

      {geciciSifre !== null && (
        <GeciciSifreKutusu
          ad={geciciSifre.ad}
          sifre={geciciSifre.sifre}
          kapat={() => geciciSifreYaz(null)}
        />
      )}

      <Tablo
        sutunlar={sutunlar}
        satirlar={liste.data?.kayitlar ?? []}
        anahtar={(k) => k.kullaniciId}
        bekliyor={liste.isPending}
        bosMetin="Bu süzgeçle hesap bulunamadı."
      />

      <Sayfalama
        sayfa={sayfa}
        sayfaBoyutu={sayfaBoyutu}
        toplam={liste.data?.toplam ?? 0}
        sayfayaGit={sayfayaGit}
        bekliyor={liste.isFetching}
      />

      {sifirlanan !== null && (
        <GerekceDiyalogu
          baslik={`${sifirlanan.adSoyad} — şifre sıfırla`}
          aciklama="Yeni geçici şifre üretilir ve İLK GİRİŞTE değiştirilmesi zorunlu olur."
          etiket="Gerekçe"
          ipucu='İsteğe bağlı ama önerilir: "telefonda talep etti, kimliği doğrulandı" gibi.'
          onayEtiketi="Sıfırla"
          zorunluMu={false}
          enCokKarakter={500}
          bekliyor={sifirla.isPending}
          hata={sifirla.error}
          kapat={() => sifirlananYaz(null)}
          onayla={(gerekce) => sifirla.mutate({ k: sifirlanan, gerekce })}
        />
      )}

      {rolAcik !== null && <RolDiyalogu kullanici={rolAcik} kapat={() => rolAcikYaz(null)} />}
    </div>
  );
}

/* ------------------------------------------------------------------ */

/**
 * GEÇİCİ ŞİFRE BİR KEZ GÖSTERİLİR.
 *
 * Sunucu şifreyi yalnız yanıtta döndürüyor, hiçbir yerde saklamıyor.
 * Kapatılırsa bir daha görülemez ve yeniden sıfırlamak gerekir — kutu bu
 * yüzden belirgin ve elle kapatılıyor.
 */
function GeciciSifreKutusu({ ad, sifre, kapat }: { ad: string; sifre: string; kapat: () => void }) {
  return (
    <section className="flex flex-wrap items-center justify-between gap-4 rounded-md border border-emerald-300 bg-emerald-50 p-4">
      <div>
        <h3 className="text-sm font-semibold text-emerald-900">{ad} için geçici şifre üretildi</h3>
        <p className="mt-1 text-xs text-emerald-800">
          Bu şifre BİR KEZ gösterilir ve saklanmaz. Kullanıcıya iletin; ilk girişte değiştirmesi
          zorunlu.
        </p>
        <code className="mt-2 block select-all rounded border border-emerald-300 bg-white px-3 py-2 font-mono text-lg text-slate-900">
          {sifre}
        </code>
      </div>
      <Dugme tur="ikincil" tikla={kapat} cocuk="Kapat" />
    </section>
  );
}

function Onay({
  etiket,
  secili,
  degisti,
}: {
  etiket: string;
  secili: boolean;
  degisti: (v: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-2 pb-2 text-sm text-slate-700">
      <input
        type="checkbox"
        checked={secili}
        onChange={(olay) => degisti(olay.target.checked)}
        className="rounded border-slate-300"
      />
      {etiket}
    </label>
  );
}

function Eylemci({
  etiket,
  tikla,
  tehlike = false,
}: {
  etiket: string;
  tikla: () => void;
  tehlike?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={tikla}
      className={`rounded px-2 py-1 text-xs ${
        tehlike ? 'text-red-700 hover:bg-red-50' : 'text-kurum-700 hover:bg-kurum-50'
      }`}
    >
      {etiket}
    </button>
  );
}

/* ------------------------------------------------------------------ */
/* Roller                                                              */
/* ------------------------------------------------------------------ */

/**
 * ROL YÖNETİMİ — yalnız `SISTEM_YONET`.
 *
 * Rol yetki demektir. Personel işlemleri yapan biri (`PERSONEL_YONET`)
 * kendine ya da başkasına rol veremez; verebilseydi yetki sınırı kendi
 * kendini genişletebilir hale gelirdi.
 *
 * ROL SONLANDIRILIR, SİLİNMEZ: bitiş tarihi yazılır ve geçmişte o rolün
 * hangi tarihler arasında geçerli olduğu denetimde görülebilir kalır.
 */
function RolDiyalogu({ kullanici, kapat }: { kullanici: KullaniciOzeti; kapat: () => void }) {
  const sorguIstemcisi = useQueryClient();

  const [rolKod, rolKodYaz] = useState('');
  const [bitis, bitisYaz] = useState('');

  const tazele = () => sorguIstemcisi.invalidateQueries({ queryKey: ['kullanici'] });

  const ata = useMutation({
    mutationFn: () =>
      rolAta(kullanici.kullaniciId, {
        rolKod,
        // Boş bırakılırsa SÜRESİZ. Bitiş verilirse rol süreli olur —
        // vekâlet ve geçici görev için.
        gecerlilikBitis: bitis === '' ? undefined : bitis,
      }),
    onSuccess: async () => {
      rolKodYaz('');
      bitisYaz('');
      await tazele();
    },
  });

  const kaldir = useMutation({
    mutationFn: (kod: string) => rolSonlandir(kullanici.kullaniciId, kod),
    onSuccess: tazele,
  });

  const eklenebilir = ROLLER.filter((r) => !kullanici.roller.includes(r.kod));

  return (
    <Diyalog
      baslik={`${kullanici.adSoyad} — roller`}
      aciklama={`${kullanici.kullaniciAdi} · rol yetki demektir, yalnız sistem yöneticisi verebilir.`}
      kapat={kapat}
      genis
      cocuk={
        <div className="space-y-4">
          <section>
            <h3 className="text-sm font-semibold text-slate-800">Mevcut roller</h3>
            {kullanici.roller.length === 0 ? (
              <p className="mt-1 text-sm text-slate-500">
                Rol yok. Yetkiler yalnız pozisyondan geliyor.
              </p>
            ) : (
              <ul className="mt-2 divide-y divide-slate-100 rounded-md border border-slate-200">
                {kullanici.roller.map((r) => {
                  const bilgi = ROLLER.find((x) => x.kod === r);
                  return (
                    <li key={r} className="flex items-center justify-between gap-3 px-3 py-2">
                      <span className="text-sm">
                        <span className="block font-medium text-slate-900">{bilgi?.ad ?? r}</span>
                        {bilgi && (
                          <span className="block text-xs text-slate-500">{bilgi.aciklama}</span>
                        )}
                      </span>
                      <button
                        type="button"
                        onClick={() => kaldir.mutate(r)}
                        className="whitespace-nowrap rounded px-2 py-1 text-xs text-red-700 hover:bg-red-50"
                      >
                        Sonlandır
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          <HataKutusu hata={kaldir.error} />

          {eklenebilir.length > 0 && (
            <section className="space-y-3 rounded-md border border-slate-200 bg-slate-50 p-3">
              <h3 className="text-sm font-semibold text-slate-800">Rol ekle</h3>

              <label className="block">
                <span className="mb-1 block text-sm font-medium text-slate-700">Rol</span>
                <select
                  value={rolKod}
                  onChange={(olay) => rolKodYaz(olay.target.value)}
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-kurum-500 focus:ring-2 focus:ring-kurum-100"
                >
                  <option value="">Seçin…</option>
                  {eklenebilir.map((r) => (
                    <option key={r.kod} value={r.kod}>
                      {r.ad}
                    </option>
                  ))}
                </select>
                {rolKod !== '' && (
                  <span className="mt-1 block text-xs text-slate-500">
                    {ROLLER.find((r) => r.kod === rolKod)?.aciklama}
                  </span>
                )}
              </label>

              <Metin
                etiket="Bitiş tarihi (isteğe bağlı)"
                tur="date"
                deger={bitis}
                degisti={bitisYaz}
                ipucu="Boş bırakılırsa süresiz. Vekâlet ve geçici görevde tarih verilir."
              />

              <HataKutusu hata={ata.error} />

              <div className="flex justify-end">
                <Dugme
                  tikla={() => ata.mutate()}
                  bekliyor={ata.isPending}
                  pasif={rolKod === ''}
                  cocuk="Rolü ata"
                />
              </div>
            </section>
          )}

          <div className="flex justify-end">
            <Dugme tur="ikincil" tikla={kapat} cocuk="Kapat" />
          </div>
        </div>
      }
    />
  );
}

/* ------------------------------------------------------------------ */
/* Hesapsız personel                                                   */
/* ------------------------------------------------------------------ */

function HesapsizListe() {
  const sorguIstemcisi = useQueryClient();
  const [geciciSifre, geciciSifreYaz] = useState<{ ad: string; sifre: string } | null>(null);

  const liste = useQuery({
    queryKey: ['kullanici', 'hesapsiz'],
    queryFn: () => hesapsizPersonel(),
  });

  const ac = useMutation({
    mutationFn: (p: HesapsizPersonel) => kullaniciOlustur(p.personelId),
    onSuccess: async (sonuc) => {
      geciciSifreYaz({ ad: sonuc.kullaniciAdi, sifre: sonuc.geciciSifre });
      await sorguIstemcisi.invalidateQueries({ queryKey: ['kullanici'] });
    },
  });

  const sutunlar: Sutun<HesapsizPersonel>[] = [
    {
      baslik: 'Personel',
      hucre: (p) => (
        <span>
          <span className="block font-medium text-slate-900">{p.adSoyad}</span>
          <span className="block font-mono text-xs text-slate-500">{p.personelNo}</span>
        </span>
      ),
    },
    { baslik: 'Birim', hucre: (p) => p.birimAd },
    {
      baslik: '',
      hucre: (p) => <Eylemci etiket="Hesap aç" tikla={() => ac.mutate(p)} />,
      hizala: 'sag',
      daralt: true,
    },
  ];

  return (
    <div className="space-y-4">
      <p className="text-xs text-slate-500">
        Hesabı olmayan personel. HERKESE HESAP AÇILMASI GEREKMEZ: puantörle takip edilen personel
        sisteme hiç girmez ve hesabı olmaması normaldir.
      </p>

      <HataKutusu hata={liste.error} />
      <HataKutusu hata={ac.error} />

      {geciciSifre !== null && (
        <GeciciSifreKutusu
          ad={geciciSifre.ad}
          sifre={geciciSifre.sifre}
          kapat={() => geciciSifreYaz(null)}
        />
      )}

      <Tablo
        sutunlar={sutunlar}
        satirlar={liste.data ?? []}
        anahtar={(p) => p.personelId}
        bekliyor={liste.isPending}
        bosMetin="Hesabı olmayan personel yok."
      />
    </div>
  );
}
